import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./PricingPage.css";

// Stripe publishable key (test mode)
const stripePromise = loadStripe("pk_test_51Nxxxxxxxxxxxxxxxxxxxxx");

const plans = [
  {
    name: "Free",
    price: { monthly: { usd: 0, inr: 0 }, yearly: { usd: 0, inr: 0 } },
    features: ["Limited quizzes", "Community support", "Basic analytics"],
    ctaText: "Get Started",
    free: true,
  },
  {
    name: "Pro",
    price: { monthly: { usd: 3.4, inr: 300 }, yearly: { usd: 34, inr: 3000 } },
    features: ["Unlimited quizzes", "Priority support", "Advanced analytics", "Leaderboard"],
    ctaText: "Subscribe Now",
    popular: true,
    stripePriceIdUSD: { monthly: "price_1Nxxxx_monthly_usd", yearly: "price_1Nxxxx_yearly_usd" },
    stripePriceIdINR: { monthly: "price_1Nxxxx_monthly_inr", yearly: "price_1Nxxxx_yearly_inr" },
  },
  {
    name: "Enterprise",
    price: { monthly: { usd: "Custom", inr: "Custom" }, yearly: { usd: "Custom", inr: "Custom" } },
    features: ["Team accounts & collaboration", "Dedicated support", "Custom integrations"],
    ctaText: "Contact Us",
    custom: true,
  },
];

const CheckoutButton = ({ priceId, onSuccess }) => {
  const stripe = useStripe();

  const handleCheckout = async () => {
    if (!stripe) return;
    try {
      const { error } = await stripe.redirectToCheckout({
        lineItems: [{ price: priceId, quantity: 1 }],
        mode: "subscription",
        successUrl: window.location.href,
        cancelUrl: window.location.href,
      });
      if (error) toast.error(error.message);
    } catch (err) {
      toast.error("Payment failed");
    }
  };

  return (
    <button className="btn" onClick={handleCheckout}>
      Pay Now
    </button>
  );
};

function PricingPageInner() {
  const [billing, setBilling] = useState("monthly");
  const [currency, setCurrency] = useState("usd");
  const [prevPrices, setPrevPrices] = useState({}); // For animation

  useEffect(() => {
    // Store previous prices for animation
    const newPrev = {};
    plans.forEach(plan => {
      newPrev[plan.name] = plan.price[billing][currency];
    });
    setPrevPrices(newPrev);
  }, [billing, currency]);

  const currencySymbol = currency === "usd" ? "$" : "₹";

  const animatePriceChange = (prev, current) => {
    return prev !== current ? "price-animate" : "";
  };

  return (
    <div className="pricing-page">
      <ToastContainer />
      <header className="pricing-header">
        <h1>BodhAI Plans</h1>
        <p>Choose the plan that best fits your learning journey.</p>
        <div className="toggles">
          <div className="currency-toggle">
            <button
              className={currency === "usd" ? "active" : ""}
              onClick={() => setCurrency("usd")}
            >
              $
            </button>
            <button
              className={currency === "inr" ? "active" : ""}
              onClick={() => setCurrency("inr")}
            >
              ₹
            </button>
          </div>
          <div className="billing-toggle">
            <button
              className={billing === "monthly" ? "active" : ""}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>
            <button
              className={billing === "yearly" ? "active" : ""}
              onClick={() => setBilling("yearly")}
            >
              Yearly
            </button>
          </div>
        </div>
      </header>

      <section className="pricing-cards">
        {plans.map(plan => {
          const priceValue = plan.price[billing][currency];
          const prevPrice = prevPrices[plan.name];
          const stripePriceId = plan.stripePriceIdUSD && currency === "usd"
            ? plan.stripePriceIdUSD[billing]
            : plan.stripePriceIdINR?.[billing];

          return (
            <div key={plan.name} className={`card ${plan.popular ? "popular" : ""}`}>
              {plan.popular && <span className="badge">Most Popular</span>}
              <h2>{plan.name}</h2>
              <p className={`price ${animatePriceChange(prevPrice, priceValue)}`}>
                {typeof priceValue === "number" ? `${currencySymbol}${priceValue}` : priceValue}
                {priceValue !== "Custom" && <span>/ {billing}</span>}
              </p>
              <ul>{plan.features.map((f, i) => <li key={i}>{f}</li>)}</ul>

              {plan.free && <a href="/signup" className="btn">{plan.ctaText}</a>}
              {plan.custom && <a href="/contact" className="btn">{plan.ctaText}</a>}
              {stripePriceId && <CheckoutButton priceId={stripePriceId} />}
            </div>
          );
        })}
      </section>

      <footer className="pricing-footer">
        <p>&copy; 2025 BodhAI. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default function PricingPage() {
  return (
    <Elements stripe={stripePromise}>
      <PricingPageInner />
    </Elements>
  );
}
